import React, { useEffect, useState, FC, useCallback, useRef } from 'react';
import LoginLayout from "../components/LoginLayout";
import { FormattedMessage } from 'react-intl';
import { reduxForm, Field, InjectedFormProps, change } from 'redux-form';
import { Button } from '@mui/material';
import { required } from '../../../components/validation/ValidationUtils';
import { renderTextField } from "../components/LoginForm";
import { useNavigate } from 'react-router-dom';
import Auth from 'Auth';

interface FormValues {
  confirmationCode?: string;
}

export interface calloutStyle {
  opacity: number,
  margin: string,
  padding: string
}

const FORM_NAME = 'twoFactorAuth';

const normalizeDigits = (v?: string) => (v || '').replace(/\D+/g, '').slice(0, 6);

const gqlFetch = async (query: string, variables: any = {}) => {
  const resp = await fetch(`${process.env.REACT_APP_API || ''}/api/graphql`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${Auth.getToken()}`
    },
    body: JSON.stringify({ query, variables })
  });

  const json = await resp.json().catch(() => ({} as any));
  if (json && json.errors && json.errors.length) {
    const err: any = new Error(json.errors[0]?.message || 'graphql_error');
    err.graphQLErrors = json.errors;
    throw err;
  }
  return json?.data || {};
};

const TwoAuthPage: FC<InjectedFormProps<FormValues>> = (props) => {
  const { handleSubmit } = props;
  const navigate = useNavigate();

  const [calloutStyle, setCalloutStyleState] = useState<calloutStyle>({
    opacity: 0,
    margin: '0px',
    padding: '0px'
  });

  const [errorState, setErrorState] = useState<boolean>(false);
  const [canRender, setCanRenderState] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [messageKey, setMessageKey] = useState<string>(''); // i18n key

  const [rememberDevice, setRememberDevice] = useState<boolean>(false);

  const isMountedRef = useRef(true); //still alive?

  useEffect(() => {
    isMountedRef.current = true;
  
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  

  const flash = useCallback((key: string, isError: boolean = false) => {
    setMessageKey(key);
    setErrorState(isError);
    setCalloutStyleState({
      opacity: 1,
      margin: '20px 0',
      padding: '20px'
    });
    window.setTimeout(() => {
      setCalloutStyleState({
        opacity: 0,
        margin: '0',
        padding: '0'
      });
    }, 8000);
  }, []);

  const requestCode = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      setIsSending(true);
      const data = await gqlFetch(`
        mutation {
          requestTwoFactorCode {
            success
            expiresAt
            trusted
            id_token
          }
        }
      `);
      if (!isMountedRef.current) return;

      const res = data?.requestTwoFactorCode;

      if (res?.trusted) {
        if (res.id_token) {
          Auth.authenticateUser(res.id_token);
        }
      
        navigate((props as any).nextPath || '/', { replace: true });
        return;
      }

      if (res?.success) {
        flash('verification.Code.Sent', false);
      } else {
        flash('twoFA.Request.Failed', true);
      }
    } catch (e: any) {
      if (!isMountedRef.current) return;
      const key = e?.message === 'unauthorized' ? 'unauthorized' : 'twoFARequestFailed';
      flash(key, true);
    } finally {
      if (!isMountedRef.current) return;
      setIsSending(false);
    }
  }, [flash, navigate, props]);

  const onSubmit = async (values: FormValues) => {
    const raw = values.confirmationCode || '';
    const code = normalizeDigits(raw);
    if (code.length !== 6) {
      flash('invalid_code_format', true);
      (props as any).dispatch?.(change(FORM_NAME, 'confirmationCode', code));
      return;
    }

    try {
      setIsVerifying(true);

      const data = await gqlFetch(
        `
        mutation Verify($code: String!, $remember: Boolean) {
          verifyTwoFactorCode(code: $code, rememberDevice: $remember) {
            ok
            id_token   # IMPORTANT: id_token, not token
          }
        }
        `,
        { code, remember: rememberDevice }
      );
      if (!isMountedRef.current) return;

      const res = data?.verifyTwoFactorCode;

      if (res?.ok) {
        // Server gave us the upgraded post-2FA JWT
        if (res.id_token) {
          Auth.authenticateUser(res.id_token);
        }
        navigate((props as any).nextPath || '/');
        return;
      }

      flash('invalidVerificationCode', true);
    } catch (e: any) {
      if (!isMountedRef.current) return;

      const map: Record<string, string> = {
        expired: 'twoFAExpired',
        mismatch: 'invalidVerificationCode',
        no_code: 'twoFANotRequested',
        invalid_code_format: 'invalid_code_format',
        unauthorized: 'unauthorized',
      };
      const key = map[e?.message] || 'invalidVerificationCode';
      flash(key, true);
    } finally {
      if (!isMountedRef.current) return;
      setIsVerifying(false);
    }
  };


  const onKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      // submit handled by form onSubmit
    }
  };

  useEffect(() => {
    if (!Auth.isUserAuthenticated()) {
      navigate('/login');
      return;
    }
    setCanRenderState(true);
  }, [navigate]);


  if (!canRender) return null;

  return (
    <LoginLayout>
      <div className="body-inner">
        <div className="card bg-color-light">
          <div className="card-content theme-light">
            <section className="logo text-center">
              <h1 style={{ color: '#0054ff' }}><FormattedMessage id="appName" /></h1>
              <p><FormattedMessage id="enter.verification.code" /></p>
            </section>

            <form className="form-horizontal" onSubmit={handleSubmit(onSubmit)}>
              <fieldset>
                <div className="form-group">
                  <Field
                    component={renderTextField}
                    label={<FormattedMessage id="confirmation.code" />}
                    name="confirmationCode"
                    margin="normal"
                    validate={required}
                    fullWidth={true}
                    onKeyUp={onKeyUp}
                    autoFocus
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      maxLength: 6,
                      autoComplete: 'one-time-code'
                    }}
                    normalize={normalizeDigits}
                  />
                </div>

                <div className="form-group" style={{ marginTop: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={rememberDevice}
                      onChange={(e) => setRememberDevice(e.target.checked)}
                    />
                    <span>
                      <FormattedMessage
                        id="remember.this.device"
                        defaultMessage="Trust this device for 14 days"
                      />
                    </span>
                  </label>
                </div>
              </fieldset>

              <div
                className="card-action no-border text-right"
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <Button type="button" onClick={requestCode} disabled={isSending}>
                  {isSending ? <FormattedMessage id="sending" /> : <FormattedMessage id="resend" />}
                </Button>
                <Button type="submit" color="primary" disabled={isVerifying}>
                  {isVerifying ? <FormattedMessage id="verifying" /> : <FormattedMessage id="verify" />}
                </Button>
              </div>
            </form>

            <div
              style={{
                opacity: calloutStyle.opacity,
                margin: calloutStyle.margin,
                padding: calloutStyle.padding,
                transition: `opacity 0.5s ease-in-out, max-height 0.5s ease-in-out, padding 0.5s ease-in-out, margin 0.5s ease-in-out`
              }}
              className={`callout ${errorState ? 'callout-danger' : 'callout-success'} no-margin-bottom`}
            >
              <p>
                <FormattedMessage id={messageKey || (errorState ? 'invalid.verification.code' : 'verification.code.sent')} />
              </p>
            </div>
          </div>
        </div>
      </div>
    </LoginLayout>
  );
};

export default reduxForm<FormValues>({
  form: 'twoFactorAuth',
  validate: (values: FormValues) => {
    const errors: any = {};
    if (!values.confirmationCode || normalizeDigits(values.confirmationCode).length !== 6) {
      errors.confirmationCode = <FormattedMessage id="required" /> as any;
    }
    return errors;
  }
})(TwoAuthPage);